import type { PtoLeaveType, PtoStatus } from '@/types/database';
import type { BadgeProps } from '@/components/ui/Badge';

export const PTO_STATUS_LABELS: Record<PtoStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export const PTO_STATUS_BADGE: Record<PtoStatus, BadgeProps['variant']> = {
  pending: 'info',
  approved: 'success',
  rejected: 'warning',
  cancelled: 'muted',
};

export const PTO_LEAVE_TYPE_LABELS: Record<PtoLeaveType, string> = {
  pto: 'PTO',
  bereavement: 'Bereavement',
};

/** Relationship of the deceased to the employee (bereavement leave). */
export const BEREAVEMENT_RELATIONSHIPS = [
  { value: 'spouse', label: 'Spouse / partner' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'grandchild', label: 'Grandchild' },
  { value: 'parent_in_law', label: 'Parent-in-law' },
  { value: 'sibling_in_law', label: 'Sibling-in-law' },
  { value: 'other_relative', label: 'Other relative' },
] as const;

export type BereavementRelationship =
  (typeof BEREAVEMENT_RELATIONSHIPS)[number]['value'];

export const BEREAVEMENT_RELATIONSHIP_LABELS: Record<
  BereavementRelationship,
  string
> = Object.fromEntries(
  BEREAVEMENT_RELATIONSHIPS.map((r) => [r.value, r.label]),
) as Record<BereavementRelationship, string>;

/** True when this request should reduce the annual PTO award balance. */
export function countsAgainstPtoBalance(
  leaveType: PtoLeaveType | null | undefined,
): boolean {
  // Missing leave_type (pre-migration rows / partial selects) treats as PTO.
  return leaveType == null || leaveType === 'pto';
}

/** Human-readable detail for the Reason / Type column. */
export function formatLeaveDetail(r: {
  leave_type?: PtoLeaveType | null;
  bereavement_relationship?: string | null;
  reason?: string | null;
}): string {
  if (r.leave_type === 'bereavement') {
    const relKey = r.bereavement_relationship ?? '';
    const relLabel =
      (BEREAVEMENT_RELATIONSHIP_LABELS as Record<string, string>)[relKey] ??
      relKey;
    const base = relLabel ? `Bereavement · ${relLabel}` : 'Bereavement';
    return r.reason?.trim() ? `${base} — ${r.reason.trim()}` : base;
  }
  return r.reason?.trim() || '—';
}

/**
 * Count the weekdays (Mon–Fri) in an inclusive `YYYY-MM-DD` range. Used as the
 * default PTO-day estimate when a user picks a date range; it deliberately
 * ignores public holidays (the user can override the number before
 * submitting).
 */
export function countWeekdays(startISO: string, endISO: string): number {
  if (!startISO || !endISO) return 0;
  const start = new Date(`${startISO}T00:00:00`);
  const end = new Date(`${endISO}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  if (end < start) return 0;
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/** "3 days" / "1 day" / "1.5 days". */
export function formatDays(days: number): string {
  const n = Number(days);
  const rounded = Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
  return `${rounded} ${n === 1 ? 'day' : 'days'}`;
}
